/**
 * Client API pour les classes scolaires
 * Endpoint: /api/school/classes
 * Service backend: sphere-school-service via sphere-gateway
 */

import { HttpClient } from '@sphere/shared/data-access/http'
import type {
  Class,
  CreateClassDto,
  UpdateClassDto,
  LightClass,
} from '@sphere/school/domain'
import type { ClassStats } from '@sphere/school/domain'
import type {
  IOffsetPaginationResponse,
  SortOrder,
} from '@sphere/shared/domain'

/** Résultat d'une opération d'ajout de membres en masse */
export interface BulkAddMembersResult {
  added: number
  skipped: number
  errors: string[]
  memberships: unknown[]
}

const BASE_PATH = '/school/classes'

export const classesClient = {
  /**
   * Vérifie si l'utilisateur peut créer une classe
   * @param establishmentId - ID de l'établissement (optionnel)
   * @returns true si l'utilisateur peut créer une classe
   */
  async canCreate(establishmentId?: string): Promise<boolean> {
    const { data } = await HttpClient.get<{ canCreate: boolean }>(
      `${BASE_PATH}/can-create`,
      { params: establishmentId ? { establishmentId } : undefined }
    )
    return data.canCreate
  },

  /**
   * Liste toutes les classes accessibles
   * Backend filtre automatiquement selon le scope utilisateur
   *
   * @param params - Filtres optionnels
   * @param params.establishmentId - Filtre par établissement
   * @param params.page - Numéro de page
   * @param params.limit - Nombre d'éléments par page
   * @param params.sort - Champ de tri
   * @param params.order - Ordre de tri
   * @param params.include - Relations à inclure (ex: 'groups')
   * @param params.q - Recherche textuelle
   */
  async list(params?: {
    establishmentId?: string
    levelExternalId?: string
    sectionId?: string
    statusId?: string
    schoolYearId?: string
    page?: number
    limit?: number
    sort?: string
    order?: SortOrder
    include?: string
    q?: string
  }): Promise<IOffsetPaginationResponse<Class>> {
    const { data } = await HttpClient.get<IOffsetPaginationResponse<Class>>(
      BASE_PATH,
      { params }
    )
    return data
  },

  /**
   * Récupère plusieurs classes en mode léger (id + nom uniquement).
   * Vérifie l'accès côté backend pour chaque classe.
   * @param classIds - IDs des classes à récupérer
   */
  async getBatchLight(classIds: string[]): Promise<LightClass[]> {
    if (!classIds || classIds.length === 0) return []
    const { data } = await HttpClient.post<LightClass[]>(
      `${BASE_PATH}/batch`,
      { classIds }
    )
    return data
  },

  /**
   * Récupère une classe par ID
   */
  async getById(id: string): Promise<Class> {
    const { data } = await HttpClient.get<Class>(`${BASE_PATH}/${id}`)
    return data
  },

  /**
   * Crée une nouvelle classe
   * Nécessite: capabilities.canCreateClass sur l'établissement
   */
  async create(dto: CreateClassDto): Promise<Class> {
    const { data } = await HttpClient.post<Class>(BASE_PATH, dto)
    return data
  },

  /**
   * Met à jour une classe
   * Nécessite: capabilities.canEdit sur la classe
   */
  async update(id: string, dto: UpdateClassDto): Promise<Class> {
    const { data } = await HttpClient.patch<Class>(`${BASE_PATH}/${id}`, dto)
    return data
  },

  /**
   * Supprime une classe
   * Nécessite: capabilities.canDelete sur la classe
   */
  async delete(id: string): Promise<void> {
    await HttpClient.delete(`${BASE_PATH}/${id}`)
  },

  /**
   * Récupère les statistiques d'une classe
   * (nombre d'élèves, matières, heures/semaine, etc.)
   */
  async getStats(id: string): Promise<ClassStats> {
    const { data } = await HttpClient.get<ClassStats>(
      `${BASE_PATH}/${id}/stats`
    )
    return data
  },

  /**
   * Ajoute des membres à une classe (bulk)
   * @param id - ID de la classe
   * @param userIds - IDs des utilisateurs à ajouter
   * @param roleCode - Code du rôle de classe (STUDENT, TEACHER, MAIN_TEACHER, DELEGATE)
   * @returns Résultat avec compteurs (added, skipped, errors)
   */
  async addMembers(
    id: string,
    userIds: string[],
    roleCode: string = 'STUDENT'
  ): Promise<BulkAddMembersResult> {
    const { data } = await HttpClient.post<BulkAddMembersResult>(
      `${BASE_PATH}/${id}/members`,
      { userIds, roleCode }
    )
    return data
  },

  /**
   * Supprime un membre d'une classe (détache le rattachement, ne supprime pas l'utilisateur)
   * @param membershipId - ID du membership à supprimer (pas l'ID de l'utilisateur)
   */
  async removeMember(membershipId: string): Promise<void> {
    await HttpClient.delete(`/school/class-memberships/${membershipId}`)
  },
}
